import { Button, Grid, Header } from '@mairistem/composants';
import { type Exercice, Sens } from '@mairistem/comptabilite';
import { formatMontant } from '@mairistem/finances-utils';
import React from 'react';
import type Financement from '../../../interfaces/common/financement';
import type ProImputation from '../../../interfaces/common/proImputation';
import type Projet from '../../../interfaces/common/projet';
import ComposantItem from './ComposantItem';
import FinancementItem from './FinancementItem';
import './FinancementList.css';

interface FinancementsListProps {
	autorisation: Projet;
	decision: number;
	exercices: Exercice[];
	exercicesGestionTva: Set<number>;
	exercicesReportFonc: Set<number>;
	hasEvenements: boolean;
	onAddFinancement: (identifiantComposant?: number | string) => void;
	onAddProImputation: (
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => void;
	onChangeFinancement: (
		data: Partial<Financement>,
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => void;
	onChangeProImputation: (
		data: Partial<ProImputation>,
		identifiantProImputation: number | string,
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => void;
	onRemoveFinancement: (
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => void;
	onRemoveProImputation: (
		identifiantProImputation: number | string,
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => void;
	sens: string;
}

const FinancementsList = ({
	autorisation,
	decision,
	exercices,
	exercicesGestionTva,
	exercicesReportFonc,
	hasEvenements,
	onAddFinancement,
	onAddProImputation,
	onChangeFinancement,
	onChangeProImputation,
	onRemoveFinancement,
	onRemoveProImputation,
	sens,
}: FinancementsListProps) =>
	hasEvenements ? (
		autorisation.composants?.map((composant) => (
			<ComposantItem
				composant={composant}
				decision={decision}
				exercices={exercices}
				exercicesGestionTva={exercicesGestionTva}
				exercicesReportFonc={exercicesReportFonc}
				key={composant.identifiant}
				onAddFinancement={() => onAddFinancement(composant.identifiant)}
				onAddProImputation={(identifiantFinancement) =>
					onAddProImputation(identifiantFinancement, composant.identifiant)
				}
				onChangeFinancement={(data, identifiantFinancement) =>
					onChangeFinancement(
						data,
						identifiantFinancement,
						composant.identifiant,
					)
				}
				onChangeProImputation={(
					data,
					identifiantProImputation,
					identifiantFinancement,
				) =>
					onChangeProImputation(
						data,
						identifiantProImputation,
						identifiantFinancement,
						composant.identifiant,
					)
				}
				onRemoveFinancement={(identifiantFinancement) =>
					onRemoveFinancement(identifiantFinancement, composant.identifiant)
				}
				onRemoveProImputation={(
					identifiantProImputation,
					identifiantFinancement,
				) =>
					onRemoveProImputation(
						identifiantProImputation,
						identifiantFinancement,
						composant.identifiant,
					)
				}
				sens={sens}
			/>
		))
	) : (
		<>
			<Grid>
				<Grid.Row>
					<Grid.Column verticalAlign="middle" width={13}>
						<Header
							content={`${autorisation.libelle} | ${formatMontant(autorisation.montantAutorisation)}`}
							size="small"
						/>
					</Grid.Column>
					<Grid.Column textAlign="right" verticalAlign="middle" width={3}>
						<Button
							content={`Ajouter ${sens === Sens.DEPENSE ? 'crédit' : 'ressource'}`}
							data-testid="addFinancementButton"
							icon="plus"
							onClick={() => onAddFinancement()}
							primary
							size="tiny"
						/>
					</Grid.Column>
				</Grid.Row>
			</Grid>
			{autorisation.financements?.map(
				(financement) =>
					financement.sens === sens && (
						<FinancementItem
							decision={decision}
							exercices={exercices}
							exercicesGestionTva={exercicesGestionTva}
							exercicesReportFonc={exercicesReportFonc}
							financement={financement}
							key={financement.identifiant}
							onAddProImputation={() =>
								onAddProImputation(financement.identifiant)
							}
							onChangeFinancement={(data) =>
								onChangeFinancement(data, financement.identifiant)
							}
							onChangeProImputation={(data, identiantProImputation) =>
								onChangeProImputation(
									data,
									identiantProImputation,
									financement.identifiant,
								)
							}
							onRemoveFinancement={() =>
								onRemoveFinancement(financement.identifiant)
							}
							onRemoveProImputation={(identifiantProImputation) =>
								onRemoveProImputation(
									identifiantProImputation,
									financement.identifiant,
								)
							}
						/>
					),
			)}
		</>
	);

export default FinancementsList;
